import { useState, useEffect, useCallback } from 'react'
import { format, startOfWeek, addDays, addWeeks, subWeeks } from 'date-fns'
import toast from 'react-hot-toast'
import { api } from '../utils/api'
import { useAuth } from '../contexts/AuthContext'
import CalendarDay from '../components/CalendarDay'
import MealDetailDrawer from '../components/MealDetailDrawer'
import AddMealModal from '../components/AddMealModal'

export default function CalendarPage() {
  const { user } = useAuth()
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 0 }))
  const [entries, setEntries] = useState({})
  const [meals, setMeals] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedDate, setSelectedDate] = useState(null)
  const [addingDate, setAddingDate] = useState(null)

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const start = format(weekStart, 'yyyy-MM-dd')
      const end = format(addDays(weekStart, 6), 'yyyy-MM-dd')
      const [weekData, mealsData] = await Promise.all([
        api.getWeek(start, end),
        api.listMeals()
      ])
      const entryMap = {}
      for (const entry of weekData.entries) {
        entryMap[entry.date] = entry
      }
      setEntries(entryMap)
      setMeals(mealsData.meals)
    } catch (err) {
      toast.error('Failed to load week')
    } finally {
      setLoading(false)
    }
  }, [weekStart])

  useEffect(() => { loadData() }, [loadData])

  async function handleAgree(date) {
    try {
      const result = await api.agreeMeal(date)
      setEntries(prev => ({ ...prev, [date]: result.entry }))
      toast.success('Agreed! 🎉')
      setSelectedDate(null)
    } catch (err) {
      toast.error(err.message)
    }
  }

  async function handleWithdraw(date) {
    try {
      const result = await api.withdrawMeal(date)
      setEntries(prev => ({ ...prev, [date]: result.entry }))
      toast.success('Removed')
      setSelectedDate(null)
    } catch (err) {
      toast.error(err.message)
    }
  }

  async function handlePropose(date, mealId, dayNotes) {
    try {
      const result = await api.proposeMeal(date, mealId, dayNotes)
      setEntries(prev => ({ ...prev, [date]: result.entry }))
      toast.success('Proposed!')
      setAddingDate(null)
    } catch (err) {
      toast.error(err.message)
    }
  }

  const selectedEntry = selectedDate ? entries[selectedDate] : null
  const selectedMeal = selectedEntry
    ? meals.find(m => m.mealId === (selectedEntry.agreedMealId || selectedEntry.proposal?.mealId))
    : null
  const counterMeal = selectedEntry?.counter
    ? meals.find(m => m.mealId === selectedEntry.counter.mealId)
    : null

  return (
    <div className="p-4">
      {/* Week nav */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => setWeekStart(w => subWeeks(w, 1))} className="p-2 rounded-lg hover:bg-gray-100">
          ‹
        </button>
        <div className="text-center">
          <p className="font-semibold text-gray-900">
            {format(weekStart, 'MMM d')} – {format(addDays(weekStart, 6), 'MMM d, yyyy')}
          </p>
        </div>
        <button onClick={() => setWeekStart(w => addWeeks(w, 1))} className="p-2 rounded-lg hover:bg-gray-100">
          ›
        </button>
      </div>

      {/* Today button */}
      <div className="flex justify-center mb-4">
        <button
          onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 0 }))}
          className="text-xs text-green-600 hover:text-green-700"
        >
          This week
        </button>
      </div>

      {/* Calendar days */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {days.map(day => {
            const dateStr = format(day, 'yyyy-MM-dd')
            const entry = entries[dateStr]
            const meal = entry ? meals.find(m =>
              m.mealId === (entry.agreedMealId || entry.proposal?.mealId)
            ) : null
            return (
              <CalendarDay
                key={dateStr}
                date={day}
                entry={entry}
                meal={meal}
                currentUser={user}
                onAdd={() => setAddingDate(dateStr)}
                onClick={() => entry && entry.state !== 'empty' && setSelectedDate(dateStr)}
              />
            )
          })}
        </div>
      )}

      {/* Meal detail drawer */}
      {selectedDate && (
        <MealDetailDrawer
          date={selectedDate}
          entry={selectedEntry}
          meal={selectedMeal}
          counterMeal={counterMeal}
          currentUser={user}
          meals={meals}
          onClose={() => setSelectedDate(null)}
          onAgree={() => handleAgree(selectedDate)}
          onWithdraw={() => handleWithdraw(selectedDate)}
          onCounter={() => { setSelectedDate(null); setAddingDate(selectedDate) }}
        />
      )}

      {/* Add meal modal */}
      {addingDate && (
        <AddMealModal
          date={addingDate}
          existingEntry={entries[addingDate]}
          meals={meals}
          onClose={() => setAddingDate(null)}
          onPropose={handlePropose}
          onMealsUpdated={setMeals}
        />
      )}
    </div>
  )
}
